import draco3d from "draco3d";
import * as THREE from "three";
import { STLExporter } from "three/examples/jsm/exporters/STLExporter.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";

function parseSTLBinary(arrayBuffer) {
  const view = new DataView(arrayBuffer);
  const numTriangles = view.getUint32(80, true);
  const vertices = new Float32Array(numTriangles * 9);
  const indices = new Uint32Array(numTriangles * 3);
  let offset = 84;
  for (let i = 0; i < numTriangles; i++) {
    offset += 12;
    for (let v = 0; v < 3; v++) {
      vertices[i * 9 + v * 3 + 0] = view.getFloat32(offset, true);
      vertices[i * 9 + v * 3 + 1] = view.getFloat32(offset + 4, true);
      vertices[i * 9 + v * 3 + 2] = view.getFloat32(offset + 8, true);
      offset += 12;
    }
    offset += 2;
    indices[i * 3 + 0] = i * 3;
    indices[i * 3 + 1] = i * 3 + 1;
    indices[i * 3 + 2] = i * 3 + 2;
  }
  return { vertices, indices };
}

function parseSTLASCII(text) {
  const verts = [];
  for (const line of text.split("\n")) {
    const t = line.trim();
    if (t.startsWith("vertex ")) {
      const p = t.split(/\s+/);
      verts.push(parseFloat(p[1]), parseFloat(p[2]), parseFloat(p[3]));
    }
  }
  const numVerts = verts.length / 3;
  const indices = new Uint32Array(numVerts);
  for (let i = 0; i < numVerts; i++) indices[i] = i;
  return { vertices: new Float32Array(verts), indices };
}

function parseSTL(arrayBuffer) {
  const firstBytes = new Uint8Array(arrayBuffer, 0, Math.min(256, arrayBuffer.byteLength));
  const preview = new TextDecoder().decode(firstBytes);
  if (preview.trimStart().startsWith("solid") && preview.includes("facet")) {
    return parseSTLASCII(new TextDecoder().decode(arrayBuffer));
  }
  return parseSTLBinary(arrayBuffer);
}

function parsePLYBinary(arrayBuffer, dataStart, numVertices, numFaces, vertexProps, isLittleEndian) {
  const typeSizes = { float: 4, double: 8, int: 4, uint: 4, short: 2, ushort: 2, char: 1, uchar: 1, int8: 1, uint8: 1, int16: 2, uint16: 2, int32: 4, uint32: 4, float32: 4, float64: 8 };
  const view = new DataView(arrayBuffer);
  const xIdx = vertexProps.findIndex(p => p.name === "x");
  const yIdx = vertexProps.findIndex(p => p.name === "y");
  const zIdx = vertexProps.findIndex(p => p.name === "z");
  const stride = vertexProps.reduce((s, p) => s + (typeSizes[p.type] || 4), 0);
  const vertices = new Float32Array(numVertices * 3);
  let offset = dataStart;
  for (let i = 0; i < numVertices; i++) {
    let propOffset = offset;
    for (let pi = 0; pi < vertexProps.length; pi++) {
      const sz = typeSizes[vertexProps[pi].type] || 4;
      if (pi === xIdx) vertices[i * 3 + 0] = view.getFloat32(propOffset, isLittleEndian);
      else if (pi === yIdx) vertices[i * 3 + 1] = view.getFloat32(propOffset, isLittleEndian);
      else if (pi === zIdx) vertices[i * 3 + 2] = view.getFloat32(propOffset, isLittleEndian);
      propOffset += sz;
    }
    offset += stride;
  }
  const bytes = new Uint8Array(arrayBuffer);
  const indices = new Uint32Array(numFaces * 3);
  for (let i = 0; i < numFaces; i++) {
    offset++;
    indices[i * 3 + 0] = view.getUint32(offset, isLittleEndian); offset += 4;
    indices[i * 3 + 1] = view.getUint32(offset, isLittleEndian); offset += 4;
    indices[i * 3 + 2] = view.getUint32(offset, isLittleEndian); offset += 4;
  }
  return { vertices, indices };
}

function parsePLYASCII(text, dataStart, numVertices, numFaces) {
  const lines = text.substring(dataStart).split("\n").map(l => l.trim()).filter(l => l.length);
  const vertices = new Float32Array(numVertices * 3);
  for (let i = 0; i < numVertices; i++) {
    const p = lines[i].split(/\s+/);
    vertices[i * 3 + 0] = parseFloat(p[0]);
    vertices[i * 3 + 1] = parseFloat(p[1]);
    vertices[i * 3 + 2] = parseFloat(p[2]);
  }
  const indices = new Uint32Array(numFaces * 3);
  for (let i = 0; i < numFaces; i++) {
    const p = lines[numVertices + i].split(/\s+/);
    indices[i * 3 + 0] = parseInt(p[1]);
    indices[i * 3 + 1] = parseInt(p[2]);
    indices[i * 3 + 2] = parseInt(p[3]);
  }
  return { vertices, indices };
}

function parsePLY(arrayBuffer) {
  const bytes = new Uint8Array(arrayBuffer);
  const headerText = new TextDecoder("ascii").decode(bytes.slice(0, Math.min(8192, bytes.length)));
  const endIdx = headerText.indexOf("end_header");
  if (endIdx === -1) throw new Error("Invalid PLY: no end_header");
  let dataStart = endIdx + "end_header".length;
  while (dataStart < bytes.length && bytes[dataStart] !== 10) dataStart++;
  dataStart++;
  const lines = headerText.substring(0, endIdx).split("\n").map(l => l.trim());
  let isBinary = false;
  let isLittleEndian = true;
  let numVertices = 0;
  let numFaces = 0;
  const vertexProps = [];
  let inVertex = false;
  for (const line of lines) {
    if (line.startsWith("format binary_little_endian")) { isBinary = true; isLittleEndian = true; }
    else if (line.startsWith("format binary_big_endian")) { isBinary = true; isLittleEndian = false; }
    else if (line.startsWith("element vertex")) { numVertices = parseInt(line.split(/\s+/)[2]); inVertex = true; }
    else if (line.startsWith("element face")) { numFaces = parseInt(line.split(/\s+/)[2]); inVertex = false; }
    else if (line.startsWith("element ")) { inVertex = false; }
    else if (line.startsWith("property") && !line.includes("list") && inVertex) {
      const p = line.split(/\s+/);
      vertexProps.push({ type: p[1], name: p[2] });
    }
  }
  if (isBinary) return parsePLYBinary(arrayBuffer, dataStart, numVertices, numFaces, vertexProps, isLittleEndian);
  return parsePLYASCII(new TextDecoder().decode(arrayBuffer), dataStart, numVertices, numFaces);
}

function parseOBJ(arrayBuffer) {
  const text = new TextDecoder().decode(arrayBuffer);
  const rawVerts = [];
  const faceIndices = [];
  for (const line of text.split("\n")) {
    const t = line.trim();
    if (t.startsWith("v ")) {
      const p = t.split(/\s+/);
      rawVerts.push(parseFloat(p[1]), parseFloat(p[2]), parseFloat(p[3]));
    } else if (t.startsWith("f ")) {
      const tokens = t.split(/\s+/).slice(1);
      const faceVerts = tokens.map(tok => parseInt(tok.split("/")[0]) - 1);
      for (let i = 1; i < faceVerts.length - 1; i++) {
        faceIndices.push(faceVerts[0], faceVerts[i], faceVerts[i + 1]);
      }
    }
  }
  return { vertices: new Float32Array(rawVerts), indices: new Uint32Array(faceIndices) };
}

async function encodeGeometryToDraco(arrayBuffer, ext) {
  const geometry = ext === "ply" ? parsePLY(arrayBuffer) : ext === "obj" ? parseOBJ(arrayBuffer) : parseSTL(arrayBuffer);
  const encoderModule = await draco3d.createEncoderModule({
    locateFile: (file) => {
      if (file === "draco_encoder_nodejs.wasm") return "/draco/draco_encoder.wasm";
      return `/draco/${file}`;
    },
  });
  const encoder = new encoderModule.Encoder();
  const meshBuilder = new encoderModule.MeshBuilder();
  const mesh = new encoderModule.Mesh();
  const numFaces = geometry.indices.length / 3;
  const numPoints = geometry.vertices.length / 3;
  meshBuilder.AddFacesToMesh(mesh, numFaces, geometry.indices);
  meshBuilder.AddFloatAttributeToMesh(mesh, encoderModule.POSITION, numPoints, 3, geometry.vertices);
  encoder.SetSpeedOptions(4, 4);
  encoder.SetAttributeQuantization(encoderModule.POSITION, 14);
  encoder.SetEncodingMethod(encoderModule.MESH_EDGEBREAKER_ENCODING);
  const outputBuffer = new encoderModule.DracoInt8Array();
  const encodedLength = encoder.EncodeMeshToDracoBuffer(mesh, outputBuffer);
  if (encodedLength <= 0) {
    encoderModule.destroy(outputBuffer);
    encoderModule.destroy(mesh);
    encoderModule.destroy(meshBuilder);
    encoderModule.destroy(encoder);
    throw new Error("Draco encoding failed");
  }
  const result = new Int8Array(encodedLength);
  for (let i = 0; i < encodedLength; i++) result[i] = outputBuffer.GetValue(i);
  encoderModule.destroy(outputBuffer);
  encoderModule.destroy(mesh);
  encoderModule.destroy(meshBuilder);
  encoderModule.destroy(encoder);
  return result.buffer;
}

export async function convertFileToDraco(file) {
  const arrayBuffer = await file.arrayBuffer();
  const ext = file.name.toLowerCase().split(".").pop();
  const dracoBuffer = await encodeGeometryToDraco(arrayBuffer, ext);
  const dracoName = file.name.replace(/\.[^/.]+$/, "") + ".drc";
  return new File([dracoBuffer], dracoName, { type: "application/octet-stream" });
}

export function isDraco(arrayBuffer) {
  const bytes = new Uint8Array(arrayBuffer, 0, 5);
  return (
    bytes[0] === 68 &&
    bytes[1] === 82 &&
    bytes[2] === 65 &&
    bytes[3] === 67 &&
    bytes[4] === 79
  );
}

export async function convertDracoToSTL(arrayBuffer) {
  const loader = new DRACOLoader();
  loader.setDecoderPath("/draco/");
  loader.setDecoderConfig({ type: "wasm" });

  const geometry = await new Promise((resolve, reject) => {
    loader.parse(
      arrayBuffer,
      (geo) => resolve(geo),
      (err) => reject(err)
    );
  });

  geometry.computeVertexNormals();

  const mesh = new THREE.Mesh(geometry);
  const exporter = new STLExporter();
  const stl = exporter.parse(mesh, { binary: true });

  geometry.dispose();
  loader.dispose();

  return new Blob([stl], { type: "model/stl" });
}

export async function resolveToStlBlob(arrayBuffer) {
  if (isDraco(arrayBuffer)) {
    return { blob: await convertDracoToSTL(arrayBuffer), wasDraco: true };
  }
  return { blob: new Blob([arrayBuffer]), wasDraco: false };
}
